// Captures Chrome's `beforeinstallprompt` as early as possible (it can fire before React mounts)
// and re-broadcasts it so any component can offer a one-tap "Add to home screen".
const EVENT = "fixli:installprompt";

export function captureInstallPrompt() {
  if (typeof window === "undefined" || window.__fixliInstallCapture) return;
  window.__fixliInstallCapture = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    window.__fixliInstallPrompt = e;
    window.dispatchEvent(new CustomEvent(EVENT));
  });
  window.addEventListener("appinstalled", () => {
    window.__fixliInstallPrompt = null;
    window.dispatchEvent(new CustomEvent(EVENT));
  });
}

export function getInstallPrompt() {
  return (typeof window !== "undefined" && window.__fixliInstallPrompt) || null;
}

export function onInstallPromptChange(cb) {
  window.addEventListener(EVENT, cb);
  return () => window.removeEventListener(EVENT, cb);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true;
}

export function isIOS() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function isMobileLike() {
  if (typeof window === "undefined") return false;
  return isIOS() || /Android/i.test(navigator.userAgent || "") || window.matchMedia?.("(pointer: coarse)")?.matches;
}

/** Point the document's PWA metadata at a tenant's door so "Add to home screen" installs *their* app. */
/* Tenant front door (fixlr.app/<slug>): install prompt + head tags point at the tenant's manifest/icons. */
export function applyDoorHead({ slug, companyName, primaryColor }) {
  return applyBrandHead(`${process.env.REACT_APP_BACKEND_URL}/api/public/tenant-portal/${encodeURIComponent(slug)}`, companyName || "Workspace", primaryColor);
}

/* Client portal (/login/<portal-slug> and everything under /portal): the client's own manifest, icon and app title. */
export function applyPortalHead({ slug, companyName, primaryColor }) {
  if (!slug) return () => {};
  return applyBrandHead(`${process.env.REACT_APP_BACKEND_URL}/api/public/client-portal/${encodeURIComponent(slug)}`, companyName || "Client portal", primaryColor);
}

function applyBrandHead(base, companyName, primaryColor) {
  const saved = {};
  const setLink = (rel, href) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); saved[rel] = null; }
    else saved[rel] = el.getAttribute("href");
    el.setAttribute("href", href);
  };
  const setMeta = (name, content) => {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); saved[`meta:${name}`] = null; }
    else saved[`meta:${name}`] = el.getAttribute("content");
    el.setAttribute("content", content);
  };
  setLink("manifest", `${base}/manifest.webmanifest`);
  setLink("apple-touch-icon", `${base}/icon-180.png`);
  setMeta("apple-mobile-web-app-title", companyName);
  if (primaryColor) setMeta("theme-color", primaryColor);
  return () => {
    Object.entries(saved).forEach(([key, value]) => {
      const el = key.startsWith("meta:") ? document.querySelector(`meta[name="${key.slice(5)}"]`) : document.querySelector(`link[rel="${key}"]`);
      if (!el) return;
      if (value === null) el.remove();
      else el.setAttribute(key.startsWith("meta:") ? "content" : "href", value);
    });
  };
}
